import { isDeviceTypeCamera, isWebDataSuccess, mapWebData, webDataSuccess } from '@cobliteam/api-lib';
import type { DeviceFragment } from '@cobliteam/api-lib/src/__generated__/graphql';
import { DisablerStatus, type ESN } from '@cobliteam/common-types';
import { createSelector } from 'reselect';

import type { AppState } from '../AppState';
import { selectDisablerDevicesStatus } from '../Disabler/selectors';

const selectDevicesDomain = (state: AppState) => state.devices;

export const selectDevices = createSelector(selectDevicesDomain, (devices) => devices.devicesWebData);

export const selectDevicesWithDisablerStatus = createSelector(selectDevices, selectDisablerDevicesStatus, (devicesWebData, devicesDisablerStatusWebData) => {
  if (isWebDataSuccess(devicesWebData) && isWebDataSuccess(devicesDisablerStatusWebData)) {
    return webDataSuccess(
      devicesWebData.data.map((device) => {
        if (device.type === 'Blocker') {
          const disablerStatus = devicesDisablerStatusWebData.data.find((event) => event.deviceId === device.id);

          if (disablerStatus && disablerStatus.status === DisablerStatus.Disabled) {
            return { ...device, isBlocked: true };
          }
        }
        return device;
      })
    );
  }

  return devicesWebData;
});

const getDeviceLabel = (device: DeviceFragment): string => {
  const { vehicle, cobliId } = device;
  return `${vehicle?.licensePlate || cobliId} ${vehicle?.model?.name ? `-  ${vehicle.model.name}` : ''}`;
};

const deviceToSelectOptionType = (device: DeviceFragment) => ({
  value: device.id,
  label: getDeviceLabel(device),
});

const getDeviceOptions = (devices: DeviceFragment[]) => devices.map(deviceToSelectOptionType);

const getDashcamOptions = (devices: DeviceFragment[]) => devices.filter((device) => isDeviceTypeCamera(device.type)).map(deviceToSelectOptionType);

export const selectDeviceOptions = createSelector(selectDevices, mapWebData(getDeviceOptions));

export const selectDashcamOptions = createSelector(selectDevices, mapWebData(getDashcamOptions));

export const selectDevicesById = createSelector(
  selectDevices,
  mapWebData((devices) => new Map(devices.map((device) => [device.id, device])))
);

export const selectDeviceByEsn = createSelector(
  (state: AppState, id: ESN) => ({
    devicesWebData: selectDevices(state),
    id,
  }),
  ({ devicesWebData, id }) => (isWebDataSuccess(devicesWebData) ? devicesWebData.data.find((device) => device.id === id) : undefined)
);
